// iter167l — Shared chat-key-bundle restore/upload helper.
//
// Used by both LoginPage (with the user's password, strongest) and
// AuthContext (with the server-issued chat_recovery_secret for OAuth-only
// users). Both code-paths converge on the same outcome: the local browser
// ends up with a usable private chat key, restored or freshly generated,
// without ever showing the "lost keys" wall.

import { api } from "./api";
import {
    generateKeyPair, getLocalPrivateJwk,
    buildEncryptedKeyBundle, restoreEncryptedKeyBundle,
} from "./crypto";

/**
 * @param {string} userId
 * @param {string} secret  Either the user's password (strongest) or their
 *                          server-issued chat_recovery_secret (OAuth fallback).
 */
export async function silentRestoreOrBackupKeyBundle(userId, secret) {
    if (!userId || !secret) return;
    let serverBundle = null;
    try {
        const r = await api.get("/users/me/key-bundle");
        serverBundle = r?.data?.bundle || null;
    } catch { return; }

    const localPriv = getLocalPrivateJwk(userId);
    try {
        if (serverBundle && !localPriv) {
            // Try to decrypt with the provided secret. If it fails (wrong
            // scheme, e.g. password-encrypted bundle on an OAuth refresh —
            // shouldn't happen but defensive), silently fall through and DO
            // NOT generate a new key (would clobber the bundle). The user
            // will see the existing setup modal on /chat.
            try {
                await restoreEncryptedKeyBundle(userId, serverBundle, secret);
                return;
            } catch {
                return; // leave it to the user-facing modal
            }
        }
        if (!localPriv) {
            await generateKeyPair(userId);
        }
        // Re-encrypt + upload. Idempotent overwrite is fine because both
        // sides converge on the same private key.
        const payload = await buildEncryptedKeyBundle(userId, secret);
        await api.post("/users/me/key-bundle", payload);
    } catch {
        /* swallow — chat recovery must never break login or refresh */
    }
}
